import React from "react"
import { Helmet } from "react-helmet-async"
import { Shield, Eye, Share2, Trash2, UserCheck, AlertTriangle, Mail, Phone } from "lucide-react"

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - Anmol Furniture Bazar</title>
        <meta 
          name="description" 
          content="Our privacy policy explains how we collect, use, and protect your personal information on our platform." 
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              How we collect, use, and protect your personal information
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {/* Introduction */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-primary-600" />
                Introduction
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  This Privacy Policy describes how Anmol Furniture Bazar and its affiliates collect, use, share, 
                  protect or otherwise process your information/personal data through our website 
                  https://www.anmolfurniturebazar.com/ (Platform).
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Please note that you may be able to browse certain sections of the Platform without registering with us. 
                  We do not offer any product/service under this Platform outside India and your personal data will 
                  primarily be stored and processed in India.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-blue-800">
                    <strong>Consent:</strong> By visiting this Platform, providing your information or availing any 
                    product/service offered on the Platform, you expressly agree to be bound by the terms and conditions 
                    of this Privacy Policy, the Terms of Use and applicable laws of India.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Collection */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Eye className="w-6 h-6 mr-3 text-primary-600" />
                Information We Collect
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Personal Information Collected:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-gray-700">• Name</p>
                      <p className="text-gray-700">• Date of birth</p>
                      <p className="text-gray-700">• Address</p>
                      <p className="text-gray-700">• Phone/mobile number</p>
                      <p className="text-gray-700">• Email ID</p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-gray-700">• Proof of identity documents</p>
                      <p className="text-gray-700">• Address verification documents</p>
                      <p className="text-gray-700">• Transaction information</p>
                      <p className="text-gray-700">• Browsing behavior and preferences</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <h3 className="font-semibold text-amber-900 mb-2">Sensitive Personal Data (with consent):</h3>
                  <ul className="text-amber-800 space-y-1">
                    <li>• Bank account information</li>
                    <li>• Credit/debit card details</li>
                    <li>• Payment instrument information</li>
                    <li>• Biometric information (for specific features)</li>
                  </ul>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-green-800">
                    <strong>Your Choice:</strong> You always have the option to not provide information by choosing 
                    not to use a particular service or feature on the Platform.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Usage */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <UserCheck className="w-6 h-6 mr-3 text-primary-600" />
                How We Use Your Information
              </h2>
              
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  We use personal data to provide the services you request. To the extent we use your personal data 
                  to market to you, we will provide you the ability to opt-out of such uses.
                </p>
                
                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Specific Uses Include:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-gray-700">
                    <li>• Assist sellers in handling and fulfilling orders</li>
                    <li>• Enhance customer experience</li>
                    <li>• Resolve disputes and troubleshoot problems</li>
                    <li>• Inform about offers, products, and services</li>
                  </ul>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Customize your experience</li>
                    <li>• Detect and protect against fraud</li>
                    <li>• Enforce terms and conditions</li>
                    <li>• Conduct marketing research and analysis</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Data Sharing */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Share2 className="w-6 h-6 mr-3 text-primary-600" />
                Information Sharing
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">We may share your information with:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Our group entities, corporate entities, and affiliates</li>
                    <li>• Sellers and business partners</li>
                    <li>• Third-party service providers (logistics partners, payment processors)</li>
                    <li>• Third-party reward programs</li>
                  </ul>
                </div>

                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h3 className="font-semibold text-red-900 mb-2">Legal Disclosures:</h3>
                  <p className="text-red-700 text-sm">
                    We may disclose personal data to government agencies or law enforcement if required by law, 
                    court orders, or to protect rights, property, or safety of our users and the general public.
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-blue-800">
                    <strong>Opt-out:</strong> You can explicitly opt-out of marketing communications from our 
                    entities and affiliates.
                  </p>
                </div>
              </div>
            </div>

            {/* Security & Data Protection */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-green-600" />
                Security Precautions
              </h2>
              
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  To protect your personal data from unauthorized access, disclosure, loss or misuse, we adopt 
                  reasonable security practices and procedures. We adhere to security guidelines to protect your 
                  account information against unauthorized access.
                </p>
                
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-yellow-900 mb-1">Your Responsibility:</h3>
                      <p className="text-yellow-800 text-sm">
                        Users are responsible for ensuring the protection of login and password records for their account. 
                        Data transmission over the internet cannot always be guaranteed as completely secure.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Rights */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Trash2 className="w-6 h-6 mr-3 text-primary-600" />
                Your Data Rights
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Account Deletion & Data Retention:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• You can delete your account by visiting your profile and settings</li>
                    <li>• Account deletion results in losing all information related to your account</li>
                    <li>• We may delay deletion if there are pending grievances, claims, or shipments</li>
                    <li>• We retain personal data only as long as required for the purpose collected</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Access & Update Rights:</h3>
                  <p className="text-gray-700">
                    You may access, rectify, and update your personal data directly through the functionalities 
                    provided on the Platform.
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-2">Consent Withdrawal:</h3>
                  <p className="text-blue-700 text-sm">
                    You can withdraw your consent by writing to our Grievance Officer with the subject line 
                    "Withdrawal of consent for processing personal data". Note that withdrawal will not be retrospective 
                    and we may restrict services that require such information.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Consent */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Phone className="w-6 h-6 mr-3 text-primary-600" />
                Communication Consent
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  By providing your personal data, you consent to us (including our corporate entities, affiliates, 
                  partners) contacting you through:
                </p>
                <ul className="text-gray-700 space-y-1 ml-6">
                  <li>SMS</li>
                  <li>Instant messaging apps</li>
                  <li>Phone calls</li>
                  <li>Email</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  These communications will be for purposes specified in this Privacy Policy.
                </p>
              </div>
            </div>

            {/* Security Warning */}
            <div className="bg-red-50 p-6 rounded-xl border border-red-200">
              <h2 className="text-xl font-semibold text-red-900 mb-4 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Security Alert
              </h2>
              <div className="text-red-800">
                <p className="mb-3">
                  <strong>Important:</strong> If you receive an email or call from someone claiming to be 
                  Anmol Furniture Bazar seeking personal data like:
                </p>
                <ul className="space-y-1 ml-6 mb-3">
                  <li>• Debit/credit card PIN</li>
                  <li>• Net-banking password</li>
                  <li>• Mobile banking password</li>
                </ul>
                <p className="font-semibold">
                  NEVER provide such information. If you have already revealed such information, 
                  report it immediately to an appropriate law enforcement agency.
                </p>
              </div>
            </div>

            {/* Policy Updates */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Changes to Privacy Policy</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  Please check our Privacy Policy periodically for changes. We may update this Privacy Policy 
                  to reflect changes to our information practices. We may alert/notify you about significant 
                  changes to the Privacy Policy, as may be required under applicable laws.
                </p>
              </div>
            </div>

            {/* Grievance Officer */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Mail className="w-6 h-6 mr-3 text-primary-600" />
                Grievance Officer
              </h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-4">
                  For any privacy-related concerns or to exercise your data rights, you can contact our Grievance Officer:
                </p>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Company:</strong> Anmol Furniture Bazar</p>
                  <p><strong>Location:</strong> Virar</p>
                  <p><strong>Contact:</strong> Use the contact information provided on our website</p>
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <Phone className="w-12 h-12 text-primary-600 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Privacy Questions?</h2>
              <p className="text-gray-600 mb-6">
                Contact us for any privacy-related queries or concerns
              </p>
              <div className="space-y-2">
                <p className="text-lg"><span className="font-semibold">Phone:</span> Available Monday - Friday (9:00 AM - 6:00 PM)</p>
                <p className="text-lg"><span className="font-semibold">Website:</span> https://www.anmolfurniturebazar.com/</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PrivacyPolicy